// Session AFW: "Continue offline" -- a read-only view of the engineer's cached job list, reachable
// from the engineer sign-in screen WITHOUT authenticating. It exists so an engineer who lands on
// the login screen while on site with poor signal is never completely locked out of their current
// job's details.
//
// This route is registered OUTSIDE ProtectedRoute, so it must read the cache directly (getCachedJobs
// via loadMerged) and render everything INLINE: the guarded /engineer/jobs/:id detail page would
// bounce an unauthenticated engineer straight back to login, so we cannot link there. The engineer
// can read the job details and the site access instructions (Session AFW), but cannot submit work
// until they sign in -- there are no complete/submit controls here.
//
// Session ASB, the third review: what this view shows are copies a server-ended ENGINEER session
// kept (src/lib/auth.tsx), and they EXPIRE: seven days after they were kept with nobody signing in,
// they are wiped. So the view asks expireKeptOfflineCopies FIRST, every time it reads, and reads only
// once any wipe it starts or joins has finished: when it opens, and again whenever it comes back into
// view, since a phone left on this screen past the expiry must not go on showing what it removed.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { WifiOff } from "lucide-react";
import type { JobView } from "../../lib/offline/types";
import { loadMerged } from "../../lib/offline/jobsStore";
import { expireKeptOfflineCopies } from "../../lib/auth";
import { isOverdue } from "../../lib/engineerCalendar";
import JobTypeBadge from "../../components/JobTypeBadge";
import { jobReferenceLabel } from "../../lib/jobReference";
import { ENG_BTN_PRIMARY, ENG_CARD, ENG_CARD_OVERDUE_STYLE, DANGER_INK, dayTimeWithYear } from "./engineerUi";

function whenLabel(iso: string | null): string {
  if (!iso) return "Unscheduled";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unscheduled";
  return dayTimeWithYear(d);
}

function scheduledMs(job: JobView): number {
  if (!job.scheduledFor) return Number.MAX_SAFE_INTEGER;
  const t = new Date(job.scheduledFor).getTime();
  return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
}

// Call-outs first (reactive attendance beats a planned visit), then by scheduled time. Completed
// jobs are omitted (their work is done); a cancelled job is not shown.
export function orderOfflineJobs(jobs: JobView[]): JobView[] {
  return jobs
    .filter((j) => j.status !== "complete" && j.status !== "cancelled")
    .sort((a, b) => {
      const ac = a.type === "callout" ? 0 : 1;
      const bc = b.type === "callout" ? 0 : 1;
      if (ac !== bc) return ac - bc;
      return scheduledMs(a) - scheduledMs(b);
    });
}

function OfflineJobCard({ job }: { job: JobView }): ReactNode {
  const overdue = isOverdue(job, new Date());
  return (
    <div className={`${ENG_CARD} flex gap-3 p-3.5`} style={overdue ? ENG_CARD_OVERDUE_STYLE : undefined}>
      <div className="min-w-0 flex-1">
        <div className="mb-1.5">
          <JobTypeBadge type={job.type} equipmentAsset={job.equipmentAsset === true} />
        </div>
        <div className="flex items-start justify-between gap-2">
          <span className="truncate font-heading text-base font-bold leading-tight text-ele-strong">{job.siteName || "Site to be confirmed"}</span>
          <span className="flex-none text-[13px]" style={overdue ? { color: DANGER_INK, fontWeight: 600 } : { color: "var(--ele-text-muted)" }}>
            {whenLabel(job.scheduledFor)}
          </span>
        </div>
        <div className="mt-0.5 truncate text-[13px]" style={{ color: job.eleNumber ? "var(--ele-loler-ink)" : "var(--ele-text-muted)" }}>
          {jobReferenceLabel(job)}
        </div>
        <p className="mt-1 text-[13px] text-ele-body">
          {job.assetType}
          {job.liftType ? `, ${job.liftType}` : ""}
        </p>
        {job.siteAddress && <p className="text-[13px] text-ele-muted">{job.siteAddress}</p>}
        {job.description && <p className="mt-2 text-sm text-ele-body">{job.description}</p>}
        {job.accessDetails && (
          <div className="mt-3 rounded-lg border border-ele-amber-line bg-ele-amber-wash p-3">
            <h3 className="font-menu text-xs uppercase tracking-wider text-ele-amber-wash-ink">Site access</h3>
            <p className="mt-1 whitespace-pre-wrap text-sm text-ele-amber-wash-ink">{job.accessDetails}</p>
          </div>
        )}
      </div>
      {/* The one action on this screen, fixed at the bottom where a thumb reaches it. */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-ele-card-border bg-[var(--ele-shell)] px-4 pt-3"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto w-full max-w-md">
          <Link to="/engineer/login" className={ENG_BTN_PRIMARY}>
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

export function OfflineJobs() {
  const [jobs, setJobs] = useState<JobView[] | null>(null);

  useEffect(() => {
    let live = true;
    // Only the latest read may set the list: one started before an expiry wipe could otherwise land after one
    // started once it had finished, and put the removed copies back on the screen.
    let latest = 0;
    const load = () => {
      const mine = ++latest;
      expireKeptOfflineCopies()
        .then(() => loadMerged())
        .then((r) => {
          if (live && mine === latest) setJobs(orderOfflineJobs(r.jobs));
        })
        .catch(() => {
          if (live && mine === latest) setJobs([]);
        });
    };
    load();
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      live = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return (
    <div className="engineer-ui min-h-dvh bg-ele-base px-4 pb-28 pt-6 text-ele-body">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full" style={{ backgroundColor: "var(--ele-pill-tint-amber)" }} aria-hidden="true">
            <WifiOff size={20} color="#b8860b" />
          </span>
          <h1 className="font-heading text-2xl tracking-wide text-ele-strong">Working offline</h1>
        </div>
        <p className="mb-6 text-sm leading-relaxed text-ele-body">
          These are your saved jobs. You can read the details and site access instructions here, but you will need to sign in
          to record work. Sign in when you have signal.
        </p>
        
        {jobs === null ? (
          <p className="text-sm" style={{ color: "var(--ele-text-muted)" }}>
            Loading your saved jobs...
          </p>
        ) : jobs.length === 0 ? (
          <p className={`${ENG_CARD} p-4 text-center text-[13px]`} style={{ color: "var(--ele-text-muted)" }}>
            No saved jobs on this device. Sign in when you have signal to load your jobs.
          </p>
        ) : (
          <ul className="space-y-2.5">
            {jobs.map((job) => (
              <li key={job.id}>
                <OfflineJobCard job={job} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
